import { isMain } from '@discovr/types';
import { campaignCopy } from '@discovr/features';
import type { BulkApproveMode, BulkApprovedStatus, BulkRosterFailure } from '@discovr/features';
import { profileOfferability } from './creatorLookupStatus';
import type { CreatorsRow, CreatorsSubTab } from './types';

/** Which bulk approval the Approve button performs for the current viewer. */
export type BulkApproveVariant = 'brand-approve' | 'approve-as-brand' | 'admin-approve';

export interface BulkPermissions {
  canApproveRejectCreators?: boolean;
  canApproveAcceptedCreators?: boolean;
  canApproveOnBehalfOfBrand?: boolean;
  canRemoveOnPending?: boolean;
}

export interface BulkCapabilities {
  approveVariant: BulkApproveVariant | null;
  canDelete: boolean;
}

export const NO_BULK_CAPABILITIES: BulkCapabilities = { approveVariant: null, canDelete: false };

const EMPTY_HIDDEN_IDS: ReadonlySet<string> = new Set();

const PENDING_STATUS = 'pending';
const ACCEPTED_BY_BRAND_STATUS = 'accepted_by_brand';

/** Endpoint mode each bulk approve variant maps to. */
export const BULK_APPROVE_MODE: Record<BulkApproveVariant, BulkApproveMode> = {
  'brand-approve': 'approve',
  'admin-approve': 'approve',
  'approve-as-brand': 'approve-as-brand',
};

/** Status each variant moves a row to, matching the single-row approve. */
export const BULK_APPROVED_STATUS: Record<BulkApproveVariant, BulkApprovedStatus> = {
  'brand-approve': 'accepted_by_brand',
  'approve-as-brand': 'accepted_by_brand',
  'admin-approve': 'accepted_by_admin',
};

/** Variants whose approval carries a per-creator deliverable selection. */
export function variantNeedsDeliverables(variant: BulkApproveVariant): boolean {
  return variant !== 'admin-approve';
}

function pendingTabCapabilities(isAdminViewer: boolean, permissions: BulkPermissions): BulkCapabilities {
  if (!isAdminViewer) {
    const approveVariant = permissions.canApproveRejectCreators ? 'brand-approve' : null;
    return { approveVariant, canDelete: false };
  }
  const approveVariant = permissions.canApproveOnBehalfOfBrand ? 'approve-as-brand' : null;
  return { approveVariant, canDelete: !!permissions.canRemoveOnPending };
}

/** The single-row "Approve as admin" button needs both rights, so bulk does too. */
function approvedTabCapabilities(isAdminViewer: boolean, permissions: BulkPermissions): BulkCapabilities {
  if (!isAdminViewer || !permissions.canApproveAcceptedCreators || !permissions.canApproveRejectCreators) {
    return NO_BULK_CAPABILITIES;
  }
  return { approveVariant: 'admin-approve', canDelete: false };
}

/** Mirrors the single-row rules; other tabs and the public view get none. */
export function resolveBulkCapabilities(
  subTab: CreatorsSubTab,
  isAdminViewer: boolean,
  permissions: BulkPermissions | null | undefined,
  isPublicView: boolean,
): BulkCapabilities {
  if (isPublicView || !permissions) return NO_BULK_CAPABILITIES;
  if (subTab === 'pending') return pendingTabCapabilities(isAdminViewer, permissions);
  if (subTab === 'approved') return approvedTabCapabilities(isAdminViewer, permissions);
  return NO_BULK_CAPABILITIES;
}

export function hasBulkActions(capabilities: BulkCapabilities): boolean {
  return capabilities.approveVariant !== null || capabilities.canDelete;
}

/** Only rows the tab's bulk actions can act on get a checkbox. */
export function isRowBulkSelectable(row: CreatorsRow, subTab: CreatorsSubTab): boolean {
  if (subTab === 'pending') return row.status === PENDING_STATUS;
  if (subTab === 'approved') return row.status === ACCEPTED_BY_BRAND_STATUS;
  return false;
}

/** The row's current selection: an unsaved optimistic pick wins over the persisted one. */
export function initialDeliverableSelection(row: CreatorsRow, optimistic: string[] | undefined): string[] {
  if (optimistic !== undefined) return optimistic;
  return row.deliverables.filter((d) => d.is_selected).map((d) => d.type);
}

/** Exactly one main, not "at least one": two mains would be sent and rejected by the backend. */
export function hasMainSelected(types: readonly string[], platform: string): boolean {
  return types.filter((t) => isMain(t, platform)).length === 1;
}

/** Ids of rows whose profile can't be offered to the brand; approving them on the brand's behalf is refused. */
export function idsNotOfferable(rows: readonly CreatorsRow[]): Set<string> {
  return new Set(rows.filter((row) => !profileOfferability(row).offerable).map((row) => String(row.id)));
}

/** The selection a bulk approval sends for a row: its current pick, minus any
 *  type the (possibly refreshed) row no longer offers. */
export function approvableSelection(row: CreatorsRow, optimistic: string[] | undefined): string[] {
  const offered = new Set<string>(row.deliverables.map((d) => d.type));
  return initialDeliverableSelection(row, optimistic).filter((type) => offered.has(type));
}

/** Ids of the rows whose table selection has no commercial (main deliverable) yet. */
export function idsMissingCommercial(
  rows: readonly CreatorsRow[],
  optimisticSelections: ReadonlyMap<string, string[]>,
  platform: string,
): Set<string> {
  const missing = rows.filter((row) => {
    const id = String(row.id);
    return !hasMainSelected(approvableSelection(row, optimisticSelections.get(id)), platform);
  });
  return new Set(missing.map((row) => String(row.id)));
}

/** Next snapshot for `nextIds`: keeps rows captured on other pages, captures new ones from this page. */
export function applySelectionChange(
  previous: ReadonlyMap<string, CreatorsRow>,
  nextIds: ReadonlySet<string>,
  pageRows: readonly CreatorsRow[],
): Map<string, CreatorsRow> {
  const pageById = new Map(pageRows.map((row) => [String(row.id), row]));
  const next = new Map<string, CreatorsRow>();
  for (const id of nextIds) {
    const row = previous.get(id) ?? pageById.get(id);
    if (row) next.set(id, row);
  }
  return next;
}

/** A selection change after the cap: `added` counts the newly ticked ids that fit. */
export interface CappedSelection {
  ids: Set<string>;
  added: number;
  truncated: boolean;
}

/** Fits `next` under `max`: ids already selected stay, newly ticked ones fill the remaining room in order. */
export function capSelection(current: ReadonlySet<string>, next: ReadonlySet<string>, max: number): CappedSelection {
  const kept = [...next].filter((id) => current.has(id));
  const ticked = [...next].filter((id) => !current.has(id));
  const room = Math.max(max - kept.length, 0);
  const added = ticked.slice(0, room);
  return { ids: new Set([...kept, ...added]), added: added.length, truncated: added.length < ticked.length };
}

/** Selected ids among `goneIds`, i.e. rows a refresh deselected without the user asking. */
export function selectedAmong(selection: ReadonlyMap<string, CreatorsRow>, goneIds: ReadonlySet<string>): string[] {
  return [...goneIds].filter((id) => selection.has(id));
}

/** A page of rows as last seen, keyed by the query that produced it. */
export interface PageSnapshot {
  key: string;
  ids: ReadonlySet<string>;
}

/** Ids missing from a refetch of the same page view, i.e. rows that left the tab.
 *  A different view or an empty page (also loading) proves nothing. */
export function idsGoneFromPage(previous: PageSnapshot | null, current: PageSnapshot): Set<string> {
  if (!previous || previous.key !== current.key || current.ids.size === 0) return new Set();
  return new Set([...previous.ids].filter((id) => !current.ids.has(id)));
}

/**
 * Refreshes snapshots from fetched rows and drops rows that stopped being
 * actionable plus every id in `droppedIds` (hidden or gone from the page): with
 * no checkbox left they would linger in the count and re-submit.
 * Rows absent from `pageRows` are kept, as they may sit on another page.
 * Returns the previous map when nothing changed.
 */
export function reconcileSelection(
  previous: ReadonlyMap<string, CreatorsRow>,
  pageRows: readonly CreatorsRow[],
  subTab: CreatorsSubTab,
  droppedIds: ReadonlySet<string> = EMPTY_HIDDEN_IDS,
): ReadonlyMap<string, CreatorsRow> {
  if (previous.size === 0) return previous;
  let changed = false;
  const next = new Map(previous);
  for (const id of previous.keys()) {
    if (!droppedIds.has(id)) continue;
    next.delete(id);
    changed = true;
  }
  for (const row of pageRows) {
    const id = String(row.id);
    if (!next.has(id) || next.get(id) === row) continue;
    changed = true;
    if (isRowBulkSelectable(row, subTab)) next.set(id, row);
    else next.delete(id);
  }
  return changed ? next : previous;
}

/** Removes the given ids, keeping the previous map when none were present. */
export function withoutIds(previous: ReadonlyMap<string, CreatorsRow>, ids: readonly string[]): ReadonlyMap<string, CreatorsRow> {
  const present = ids.filter((id) => previous.has(id));
  if (present.length === 0) return previous;
  const next = new Map(previous);
  present.forEach((id) => next.delete(id));
  return next;
}

function pluralCreators(count: number): string {
  if (count === 1) return '1 creator';
  return `${count} creators`;
}

export type BulkToastTone = 'success' | 'warning' | 'error';

export interface BulkResultSummary {
  tone: BulkToastTone;
  message: string;
  description?: string;
}

const MAX_FAILURES_LISTED = 3;
const MAX_BLOCKING_NAMES_LISTED = 3;

/** Names of the creators blocking an approval, capped so a large selection keeps the toast short. */
export function describeBlockingCreators(ids: ReadonlySet<string>, nameById: ReadonlyMap<string, string>): string {
  const names = [...ids].map((id) => nameById.get(id) ?? id);
  const listed = names.slice(0, MAX_BLOCKING_NAMES_LISTED);
  return campaignCopy.creator.bulkApprove.blockingCreators(listed, names.length - listed.length);
}

function describeFailures(failed: readonly BulkRosterFailure[], nameById: ReadonlyMap<string, string>): string {
  const listed = failed
    .slice(0, MAX_FAILURES_LISTED)
    .map((f) => `${nameById.get(f.entry_id) ?? f.entry_id}: ${f.reason}`);
  const hidden = failed.length - listed.length;
  if (hidden > 0) listed.push(`+${hidden} more`);
  return listed.join('; ');
}

/**
 * Toast content for a finished bulk call. `pastVerb` is e.g. "Approved";
 * `emptyMessage` is shown when nothing succeeded.
 */
export function summarizeBulkResult(
  succeededCount: number,
  failed: readonly BulkRosterFailure[],
  nameById: ReadonlyMap<string, string>,
  pastVerb: string,
  emptyMessage: string,
): BulkResultSummary {
  const succeeded = `${pastVerb} ${pluralCreators(succeededCount)}`;
  if (failed.length === 0) return { tone: 'success', message: succeeded };
  const description = describeFailures(failed, nameById);
  if (succeededCount === 0) return { tone: 'error', message: emptyMessage, description };
  return { tone: 'warning', message: `${succeeded} · ${failed.length} failed`, description };
}
